import type { ConversationAttributeItem } from '@/entities/attributes/model/api/types';
import { ATTRIBUTE_TYPES } from '@/entities/attributes';
import type {
  AttributeFilter,
  AttributeFilterDateRange,
  AttributeFilterNumeric,
  AttributeFilterTimestampRange,
} from '@/entities/dashboards';

/**
 * Вид атрибутного фильтра, выбираемый по `Attribute.dataType`.
 * Используется как дискриминатор UI-компонента и формы значения.
 * `STRUCTURE`-атрибуты исключаются из фильтрации.
 */
export type AttributeFilterKind =
  | 'select'
  | 'multiselect'
  | 'numeric'
  | 'text'
  | 'boolean'
  | 'dateRange'
  | 'timestampRange';

/** Возвращает вид фильтра по dataType атрибута либо null, если фильтрация не поддерживается. */
export const getAttributeFilterKind = (
  attribute: ConversationAttributeItem | undefined,
): AttributeFilterKind | null => {
  if (!attribute) return null;
  switch (attribute.dataType) {
    case ATTRIBUTE_TYPES.SELECT:
      return 'select';
    case ATTRIBUTE_TYPES.MULTISELECT:
      return 'multiselect';
    // Интервал времени хранится числом секунд — отбирается числовыми границами
    case ATTRIBUTE_TYPES.TIME:
    case ATTRIBUTE_TYPES.INTEGER:
    case ATTRIBUTE_TYPES.DECIMAL:
      return 'numeric';
    case ATTRIBUTE_TYPES.TEXT:
      return 'text';
    case ATTRIBUTE_TYPES.CHECKBOX:
      return 'boolean';
    case ATTRIBUTE_TYPES.DATE:
      return 'dateRange';
    case ATTRIBUTE_TYPES.TIMESTAMP:
      return 'timestampRange';
    default:
      // STRUCTURE и неизвестные типы — фильтрация не поддерживается
      return null;
  }
};

/** Доступен ли атрибут для добавления в панель фильтров дашборда. STRUCTURE исключён. */
export const isFilterableAttribute = (attribute: ConversationAttributeItem): boolean =>
  !attribute.isDeleted && getAttributeFilterKind(attribute) !== null;

/**
 * Создаёт пустую структуру фильтра под выбранный атрибут.
 * Форма результата соответствует виду фильтра, определённому через {@link getAttributeFilterKind}.
 * Возвращает null, если атрибут не подлежит фильтрации (STRUCTURE).
 */
export const createEmptyFilter = (attribute: ConversationAttributeItem): AttributeFilter | null => {
  const kind = getAttributeFilterKind(attribute);
  if (kind === null) return null;
  const path = attribute.path;
  switch (kind) {
    case 'select':
    case 'multiselect':
      return { path, values: [] };
    case 'numeric':
      return { path };
    case 'text':
      return { path, text: '' };
    case 'boolean':
      // CHECKBOX считается применённым сразу после добавления — дефолт false
      return { path, bool: false };
    case 'dateRange':
    case 'timestampRange':
      return { path };
  }
};

/**
 * Миграция формата атрибутного фильтра при чтении из uiSettings.
 * До «Доработок СКТ — 3» элемент массива хранил ключ `code`; новый контракт требует `path`.
 * Сохранённые ранее значения переименовываются на лету при загрузке настроек.
 * TODO: удалить после следующего релиза после массовой миграции uiSettings пользователей.
 */
export const migrateLegacyAttributeFilters = (filters: AttributeFilter[] | undefined): AttributeFilter[] => {
  if (!filters) return [];
  return filters.map((filter) => {
    if ('path' in filter && filter.path) return filter;
    const legacy = filter as unknown as { code?: string };
    if (typeof legacy.code === 'string') {
      const { code: _legacyCode, ...rest } = legacy as { code: string } & Record<string, unknown>;
      return { ...(rest as object), path: legacy.code } as AttributeFilter;
    }
    return filter;
  });
};

/**
 * Проверяет, что фильтр содержит хотя бы одно осмысленное значение.
 * Пустые фильтры не отправляются на бэкенд (см. IL «Фильтрация по атрибутам дашборда»).
 *
 * - `values` — хотя бы один элемент;
 * - `text` — непустая строка после trim;
 * - `bool` — применяется всегда, если поле присутствует;
 * - `dateFrom`/`dateTo` — хотя бы одна граница задана;
 * - `min`/`max` — хотя бы одна граница задана.
 */
export const isFilterFilled = (f: AttributeFilter): boolean => {
  if ('values' in f) {
    return f.values.length > 0;
  }
  if ('text' in f) {
    return f.text.trim() !== '';
  }
  if ('bool' in f) {
    return true;
  }
  if ('dateFrom' in f || 'dateTo' in f) {
    const range = f as AttributeFilterDateRange | AttributeFilterTimestampRange;
    return Boolean(range.dateFrom) || Boolean(range.dateTo);
  }
  const numeric = f as AttributeFilterNumeric;
  return numeric.min !== undefined || numeric.max !== undefined;
};

/**
 * Сериализует список атрибутных фильтров в строку для query-параметра `attribute-filters`.
 * Возвращает `null`, если нет ни одного заполненного фильтра — параметр в этом случае не передаётся.
 */
export const serializeAttributeFilters = (filters: AttributeFilter[]): string | null => {
  const filled = filters.filter(isFilterFilled);
  if (filled.length === 0) return null;
  return JSON.stringify(filled);
};
